import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auditsAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';

/**
 * An audit's questionnaire is hidden from the questionnaire screens, so its
 * old URL (`/intelligence/qualifications/:id`, e.g. a converted questionnaire)
 * would read "not found". For the auditing customer, send it to the audit.
 *
 * @param qualificationId - the questionnaire that failed to load; null while it is loading or found
 * @returns true while the audit lookup runs (show a loader, not "not found")
 */
export function useAuditRedirect(qualificationId: number | null): boolean {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { auditLiteEnabled } = useMenuSettings();
  const eligible = Boolean(qualificationId && auditLiteEnabled && user?.platform !== 'SUPPLIER');
  const [checking, setChecking] = useState<boolean>(eligible);

  useEffect(() => {
    if (!eligible || !qualificationId) { setChecking(false); return; }
    let cancelled = false;
    setChecking(true);
    auditsAPI.list({ qualification: qualificationId })
      .then((rows) => {
        const audit = rows[0];
        if (!cancelled && audit) navigate(`/audits/${audit.id}`, { replace: true });
      })
      .catch(() => { /* no audit to go to: the page shows "not found" */ })
      .finally(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
  }, [eligible, qualificationId, navigate]);

  return eligible && checking;
}
